import React from "react";
import {
	SEGMENT_CMC,
	URL_CMC_AIRCRAFT_DATA,
	URL_CMC_OPERATOR_AIRPORT_DATA,
	URL_CMC_OPERATOR_CONTACTS_DATA,
	URL_CMC_OPERATOR_SCHEDULE_DATA,
	URL_DATA_FEEDS,
} from "../constants";
import { Hero } from "../sections/hero/Hero";
import { ProudToServe } from "../sections/proud-to-serve/ProudToServe";
import { Technology } from "../sections/Technology";
import { Testimonials } from "../../common/layout/testimonials/Testimonials";
import { ProductTabs } from "../sections/ProductTabs";
import { ProductSlider } from "../sections/ProductSlider";
import { Upgrades } from "../sections/Upgrades";
import { Trial } from "../sections/Trial";
import { Pricing } from "../sections/pricing/Pricing";
import { Faq } from "../sections/faq/Faq";
import { productPageProps } from "../../common/types/product";

export default function OperatorCapacityData({
	packageName,
	subscribeLink,
	testimonials,
}: productPageProps) {
	return (
		<>
			<Hero
				title={packageName}
				subtitle="Access detailed capacity, frequency, and market share information for operators and airports worldwide."
				packages={[
					{
						label: SEGMENT_CMC,
						features: [
							"In collaboration with OAG, we offer you detailed capacity information for any market and any need",
							"Access top operators and routes with detailed capacity overview for the specified airport",
						],
						subscribeLinkUrl: subscribeLink,
					},
				]}
			/>
			<ProudToServe />
			<ProductTabs
				title={packageName}
				tabs={[
					{
						title: "Operator Capacity",
						description: "An overview of operator’s capacity:",
						list: [
							"Weekly capacity",
							"Weekly frequency",
							"Weekly ASK (available seat kilometers)",
							"Weekly ASM (available seat miles)",
							"Weekly Top 10 airports (by frequency and capacity)",
							"Weekly Top 10 routes (by capacity, frequency, ASK, and ASM)",
						],
						visual: {
							url: "/static/img/product/commercial-operator-capacity-data/operator-profile-capacities.jpg",
						},
					},
				]}
			/>
			<ProductSlider
				description="Search operator capacity by name, origin, destination, aircraft type, alliance, service and flight type, cabin class, or time range. View results by total capacity, frequency, ASK, or ASM, and show results based on various filters."
				slides={[
					{
						title: "Origin",
						description:
							"Search for capacity by origin airport location:",
						list: [
							"Continent",
							"Country",
							"Subdivision",
							"Metro group",
							"Airport",
						],
						visual: {
							url: "/static/img/product/commercial-operator-capacity-data/capacity-search-by-origin.jpg",
							title: "Overview of seats available from United States, in the following week",
						},
					},
					{
						title: "Destination",
						description:
							"Search for capacity by destination airport location:",
						list: [
							"Continent",
							"Country",
							"Subdivision",
							"Metro group",
							"Airport",
						],
						visual: {
							url: "/static/img/product/commercial-operator-capacity-data/capacity-search-by-destination.jpg",
							title: "Overview of seats available with JFK as final destination, in the following week",
						},
					},
					{
						title: "Aircraft",
						description: "Search for capacity by:",
						list: [
							"Aircraft market group",
							"Aircraft manufacturer",
							"Aircraft family/type/variant",
							"Aircraft IATA code",
						],
						visual: {
							url: "/static/img/product/commercial-operator-capacity-data/capacity-search-by-aircraft.jpg",
							title: "Overview of seat availability on flights from Europe operated by Boeing aircraft in October 2025",
						},
					},
					{
						title: "Various Filters & Settings",
						description: (
							<>
								<p>Filter the results by:</p>
								<ul className="ticks ticks--small">
									<li>Operator name</li>
									<li>Operator&apos;s alliance</li>
									<li>Service type</li>
									<li>Flight type</li>
									<li>Cabin</li>
									<li>Date (From-To)</li>
								</ul>
								<p>Choose to display results by:</p>
								<ul className="ticks ticks--small">
									<li>Capacity</li>
									<li>Frequency</li>
									<li>ASK (available seat kilometres)</li>
									<li>ASM (available seat miles)</li>
								</ul>
								<p>View results based on:</p>
							</>
						),
						list: [
							"Operator, origin",
							"Operator, destination",
							"Operator, route",
							"Operator",
							"Route",
							"Origin",
							"Origin country",
							"Destination",
							"Destination country",
						],
						visual: {
							url: "/static/img/product/commercial-operator-capacity-data/capacity-search-by-various-filters.jpg",
							title: "Available seat capacity on routes between North America and Europe for August 2025",
						},
					},
				]}
			/>
			<Upgrades
				packageName={packageName}
				modules={[
					{
						title: "Operator & Airport Data",
						content:
							"Get access to more than 14,000 commercial operator and airport profiles, as well as start-up operators worldwide.",
						url: URL_CMC_OPERATOR_AIRPORT_DATA,
					},
					{
						title: "Operator Schedule Data",
						content:
							"Get access to schedules of the vast majority of scheduled airlines worldwide.",
						url: URL_CMC_OPERATOR_SCHEDULE_DATA,
					},
					{
						title: "Operator Contact Data",
						content:
							"Get a detailed contacts overview for almost any active as well as start-up operator worldwide.",
						url: URL_CMC_OPERATOR_CONTACTS_DATA,
					},
					{
						title: "Aircraft Data",
						content:
							"Get access to our extensive fleet database with over 82,000 aircraft currently being tracked.",
						url: URL_CMC_AIRCRAFT_DATA,
					},
				]}
			>
				<Trial subscribeLink={subscribeLink} />
			</Upgrades>
			<Testimonials testimonials={testimonials} />
			<Technology exportOptionsAvailable={false} />
			<Pricing
				packageName={packageName}
				description="Gain a comprehensive understanding of the aviation industry with detailed, up-to-date insights."
				list={[
					"In collaboration with OAG, we offer you detailed capacity information for any market and any need",
					"Access top operators and routes with detailed capacity overview for the specified airport",
				]}
				subscribeLink={subscribeLink}
				downloadsDescription="Commercial Aviation Operator Capacity Data is not available for download."
				downloadsAvailable={false}
			>
				<Trial subscribeLink={subscribeLink} />
			</Pricing>
			<Faq
				items={[
					{
						title: "Can I download these data sets?",
						content:
							"The Commercial Aviation Operator Capacity Data cannot be downloaded.",
					},
					{
						title: "Can I integrate this data as a data feed into my own system?",
						content: (
							<p>
								Yes, Commercial Aviation Operator Capacity Data
								can be purchased as a data feed for seamless
								integration into your own system. ch-aviation
								also provides full support for the integration
								process. To learn more about data feeds, click{" "}
								<a
									href={URL_DATA_FEEDS}
									target="_blank"
									rel="noopener noreferrer"
								>
									here
								</a>
								.
							</p>
						),
					},
					{
						title: "How up to date is the ch-aviation capacity data?",
						content:
							"ch-aviation has partnered with OAG Aviation Worldwide Limited, one of the leading operator schedule data providers to be able to offer this service for you. We receive weekly schedule data for the vast majority of all scheduled passenger operators and also some charter and cargo carriers and then combine it with our own ch-aviation fleet data to calculate the actual ch-aviation capacity data. While ch-aviation is undertaking a large amount of effort to correct and improve the data quality received from operators by applying a wide range of data integrity checks, we still rely on what is initially filed and provided by the operators and cannot guarantee that the capacity data is 100% accurate at any given time.",
					},
					{
						title: "How is a metro group defined?",
						content:
							"A metro group includes multiple airports in the same city or close to the same city. If you are flying to New York, it might be reasonable for you to consider a flight into “JFK” (New York John F. Kennedy International) but to fly back from EWR (Newark Liberty International) because of flight times, your plans for the visit, air fare or similar. Both airports serve the city of New York and along with La Guardia airport even closer to the city centre are therefore in the metro group for New York City (metro group code NYC). By the way, we have made it easier for you to find alternative airports for a trip as well. Just click on any airport throughout the site and we will show you airports close by that might be of interest.",
					},
				]}
			/>
		</>
	);
}
